import { ConnectProto } from "@racoon/api";
import { isShiftTerminal } from "~/shared/lib/shift-status";

/** The shift's driver as "First Last", or `fallbackName` when it has none. */
export const formatDriverName = (
  shift: ConnectProto.DriverOnShift | undefined,
  fallbackName: string,
): string => {
  const fullName = [shift?.driver?.firstName, shift?.driver?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return fullName || fallbackName;
};

/**
 * Every driver the truck has that day, planned shifts included — not only the running
 * one. A cancelled shift's driver is not on the truck.
 */
export const shiftDriverNames = (shifts: ConnectProto.DriverOnShift[]): string =>
  [
    ...new Set(
      shifts
        .filter((shift) => shift.status !== ConnectProto.DriverOnShiftStatus.CANCELLED)
        .map((shift) => formatDriverName(shift, ""))
        .filter(Boolean),
    ),
  ].join(", ");

export type DriverEntry = {
  truckId: bigint;
  /** Figma "Команда 1 и Команда 2 | ПрУ АЛМ": the driver's teams, then the truck's organization. */
  meta: string;
  /** The shift picking the row lands on. */
  shiftId: bigint;
  /** Every shift of this driver on this truck: the row stays selected on any of them. */
  shiftIds: bigint[];
  /** Empty for the truck's shifts that have no driver; the picker names that row. */
  name: string;
  truck: ConnectProto.Truck;
};

/**
 * The drivers switch lists people, not trucks: a row per driver per truck, though they
 * may hold several shifts that day — it lands on the open one, else the first. A truck's
 * driverless shifts share one row. Named drivers sort by name, the driverless rows last
 * by plate.
 */
export const buildDriverEntries = (
  vehicles: { truck: ConnectProto.Truck; shifts: ConnectProto.DriverOnShift[] }[],
  teams: ConnectProto.Team[],
  lang: string,
): DriverEntry[] => {
  const listFormat = new Intl.ListFormat(lang, { type: "conjunction" });
  return vehicles
    .flatMap(({ truck, shifts }) => {
      const byDriver = new Map<string, ConnectProto.DriverOnShift[]>();
      shifts.forEach((shift) => {
        const key = formatDriverName(shift, "")
          ? String(shift.driver?.id ?? shift.driverId)
          : "none";
        byDriver.set(key, [...(byDriver.get(key) ?? []), shift]);
      });
      return [...byDriver.values()].map((driverShifts) => {
        const landing =
          driverShifts.find((shift) => !isShiftTerminal(shift.status)) ?? driverShifts[0];
        const name = formatDriverName(landing, "");
        const driverId = String(landing.driver?.id ?? landing.driverId);
        const teamNames = name
          ? teams
              .filter((team) => team.users.some((user) => String(user.id) === driverId))
              .map((team) => team.name)
          : [];
        return {
          truckId: BigInt(truck.id),
          meta: [listFormat.format(teamNames), truck.ownerOrganization?.name]
            .filter(Boolean)
            .join("  |  "),
          shiftId: BigInt(landing.id),
          shiftIds: driverShifts.map((shift) => BigInt(shift.id)),
          name,
          truck,
        };
      });
    })
    .sort((a, b) =>
      !a.name || !b.name
        ? Number(!a.name) - Number(!b.name) ||
          a.truck.plateNumber.localeCompare(b.truck.plateNumber)
        : a.name.localeCompare(b.name),
    );
};

/** The row `delta` away from `index`, wrapping; from no row (-1) the first or the last. */
export const stepIndex = (index: number, delta: number, length: number) =>
  index === -1 ? (delta > 0 ? 0 : length - 1) : (index + delta + length) % length;
